import {
  type VENDOR_CONFIG_PAYLOAD,
  listen,
  vendorConfigMessage,
  isVendorConfigRequestMessage,
  sendLocal,
} from "@sinch/bus";
import { useEffect, useCallback } from "react";
import type { VendorConfigPlugin } from "../portals/messagemedia/vendorConfig/loadVendorConfigPlugin";

type Logo = {
  src: string;
  alt: string;
};

export const useVendorConfigHandler = (vendorConfig: VendorConfigPlugin) => {
  const getSupportLink = useCallback(() => {
    const supportLinkValue = vendorConfig?.getByKey("support-link");
    return typeof supportLinkValue === "string"
      ? supportLinkValue
      : "http://support.messagemedia.com/";
  }, [vendorConfig]);

  const getCollapsedLogo: () => Logo | undefined = useCallback(() => {
    const collapsedLogoValue = vendorConfig?.getByKey("logo-collapsed");
    if (
      collapsedLogoValue &&
      typeof collapsedLogoValue === "object" &&
      typeof collapsedLogoValue.src === "string" &&
      typeof collapsedLogoValue.alt === "string"
    ) {
      return collapsedLogoValue as Logo;
    }
    return undefined;
  }, [vendorConfig]);

  const getLogo: () => Logo | undefined = useCallback(() => {
    const logoValue = vendorConfig?.getByKey("logo");
    if (
      logoValue &&
      typeof logoValue === "object" &&
      typeof logoValue.src === "string" &&
      typeof logoValue.alt === "string"
    ) {
      return logoValue as Logo;
    }
    return undefined;
  }, [vendorConfig]);

  const getExtensionV2Config = useCallback(() => {
    const extensionsV2 = vendorConfig?.getByKey("extensions-v2");
    const hasExtensionsV2 =
      (typeof extensionsV2 === "boolean" && extensionsV2) ||
      (typeof extensionsV2 === "object" && extensionsV2.production);
    return extensionsV2 !== undefined ? !hasExtensionsV2 : false;
  }, [vendorConfig]);

  const getNumbersAndChannelsV2Config = useCallback(() => {
    const numbersAndChannelsV2 = vendorConfig?.getByKey(
      "numbers-and-channels-v2"
    );
    const hasNumbersAndChannelsV2 =
      (typeof numbersAndChannelsV2 === "boolean" && numbersAndChannelsV2) ||
      (typeof numbersAndChannelsV2 === "object" &&
        numbersAndChannelsV2.production);
    return numbersAndChannelsV2 !== undefined
      ? !hasNumbersAndChannelsV2
      : false;
  }, [vendorConfig]);

  const getVendorConfigMessage: () => VENDOR_CONFIG_PAYLOAD =
    useCallback((): VENDOR_CONFIG_PAYLOAD => {
      const collapsedLogoValue = getCollapsedLogo();
      const logoValue = getLogo();
      const extensionsV2Value = getExtensionV2Config();
      const numbersAndChannelsV2Value = getNumbersAndChannelsV2Config();

      return {
        isBrandMM: vendorConfig ? vendorConfig.isBrandMM() : false,
        isBrandSE: vendorConfig ? vendorConfig.isBrandSE() : false,
        isBrandMS: vendorConfig ? vendorConfig.isBrandMS() : false,
        isBrandSMSB: vendorConfig ? vendorConfig.isBrandSMSB() : false,
        isBrandTPG: vendorConfig ? vendorConfig.isBrandTPG() : false,
        isBrandWholeSale: vendorConfig
          ? vendorConfig.isBrandWholeSale()
          : false,
        isBrandSimpleTexting: vendorConfig
          ? vendorConfig.isBrandSimpleTexting()
          : false,
        isBrandDirectSMS: vendorConfig
          ? vendorConfig.isBrandDirectSMS()
          : false,
        isBrandMessageNet: vendorConfig
          ? vendorConfig.isBrandMessageNet()
          : false,
        isBrandMobiPost: vendorConfig ? vendorConfig.isBrandMobiPost() : false,
        isBrandSMSC: vendorConfig ? vendorConfig.isBrandSMSC() : false,
        isBrandStreetData: vendorConfig
          ? vendorConfig.isBrandStreetData()
          : false,
        isNZCarriers: vendorConfig ? vendorConfig.isNZCarriers() : false,
        supportLink: getSupportLink(),
        ...(collapsedLogoValue && { logoCollapsed: collapsedLogoValue }),
        ...(logoValue && { logo: logoValue }),
        ...(extensionsV2Value && { extensionsV2: extensionsV2Value }),
        ...(numbersAndChannelsV2Value && {
          numbersAndChannelsV2: numbersAndChannelsV2Value,
        }),
      };
    }, [
      getCollapsedLogo,
      getLogo,
      getSupportLink,
      getExtensionV2Config,
      getNumbersAndChannelsV2Config,
      vendorConfig,
    ]);

  useEffect(() => {
    const unsub = listen(() => {
      sendLocal(vendorConfigMessage(getVendorConfigMessage()));
    }, isVendorConfigRequestMessage);

    return unsub;
  }, [getVendorConfigMessage]);
};
